import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import { FiFileText, FiCheckCircle, FiShield, FiUser, FiXCircle, FiDollarSign, FiAlertTriangle } from "react-icons/fi";
import * as api from "../../api/index";

const ReviewContract = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [contractData, setContractData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Modal states for Accept and Reject confirmations
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    const fetchContract = async () => {
      try {
        setLoading(true);
        const res = await api.getContractByUniqueId(id);
        setContractData(res.data.contract);
      } catch (err) {
        console.error("Failed to load contract:", err);
        setErrorMessage(err.response?.data?.message || "Invalid or expired contract link.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchContract();
    } else if (location.state) {
      setContractData({
        _id: location.state?.contractId,
        itemTitle: location.state?.itemTitle || "Sample Item / Service",
        itemDescription: location.state?.itemDescription || "No specific conditions provided.",
        price: location.state?.price || "0.00",
        myName: location.state?.myName || "Party A",
        otherPartyName: location.state?.otherPartyName || "Party B",
        myEmail: location.state?.myEmail || "partyA@example.com",
        otherPartyEmail: location.state?.otherPartyEmail || "partyB@example.com",
        status: location.state?.status || "pending"
      });
      setLoading(false);
    } else {
      setLoading(false);
      setErrorMessage("No contract identifier found.");
    }
  }, [id, location.state]);

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Loading live contract...</p>
        </div>
      </div>
    );
  }

  if (!contractData) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center p-6">
        <div className="bg-white p-8 rounded-[2.5rem] shadow-xl text-center space-y-4 max-w-sm w-full border border-gray-100">
          <FiAlertTriangle size={32} className="text-red-500 mx-auto" />
          <h2 className="text-lg font-black text-gray-900">Contract Not Found</h2>
          <p className="text-xs text-gray-500 font-medium">{errorMessage || "The requested contract could not be retrieved."}</p>
        </div>
      </div>
    );
  }

  const currentStatus = contractData.status;

  const handleConfirmAccept = async () => {
    setShowAcceptModal(false);
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const res = await api.acceptContract(contractData._id);
      if (res.data.success) {
        navigate("/connect");
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Failed to accept contract.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmReject = async (e) => {
    e.preventDefault();
    setShowRejectModal(false);
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const res = await api.rejectContract(contractData._id, { rejectReason });
      if (res.data.success) {
        navigate("/connect");
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Failed to reject contract.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-12 relative">
      {/* 🔝 Sticky Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-center">
        <h1 className="text-lg font-black text-gray-900 tracking-tight">Review Contract</h1>
      </div>

      <div className="max-w-xl mx-auto px-6 pt-8">
        {errorMessage && (
          <div className="bg-red-50 border-2 border-red-200 text-red-700 p-4 rounded-2xl text-xs font-bold mb-6">
            {errorMessage}
          </div>
        )}
        
        {/* 🛡️ Info Banner or Status Alert */}
        {currentStatus === "pending" && (
          <div className="bg-teal-900 rounded-[2.5rem] p-6 mb-8 text-white shadow-xl shadow-teal-100 overflow-hidden relative">
            <div className="absolute right-[-10%] top-[-20%] w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                 <div className="p-2 bg-teal-800 rounded-xl"><FiShield className="text-teal-100" /></div>
                 <h2 className="text-sm font-black uppercase tracking-widest">Action Required: Review Terms</h2>
              </div>
              <p className="text-xs font-bold text-teal-50 leading-relaxed">
                {contractData.myName} has invited you to review and bind this contract agreement on OurASAP. Please verify the terms and agreed amount below before accepting.
              </p>
            </div>
          </div>
        )}

        {currentStatus === "accepted" && (
          <div className="bg-green-800 rounded-[2.5rem] p-6 mb-8 text-white shadow-xl shadow-green-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-700 rounded-xl"><FiCheckCircle className="text-white" size={20} /></div>
              <h2 className="text-sm font-black uppercase tracking-widest">Contract Accepted</h2>
            </div>
            <p className="text-xs font-medium text-green-50">
              You have successfully accepted these terms. Both parties have been notified, and digital copies have been logged securely.
            </p>
          </div>
        )}

        {currentStatus === "rejected" && (
          <div className="bg-red-800 rounded-[2.5rem] p-6 mb-8 text-white shadow-xl shadow-red-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-red-700 rounded-xl"><FiXCircle className="text-white" size={20} /></div>
              <h2 className="text-sm font-black uppercase tracking-widest">Contract Declined</h2>
            </div>
            <p className="text-xs font-medium text-red-50">
              You have rejected the terms for this transaction. The initiator has been notified to make updates or cancel.
            </p>
          </div>
        )}

        {/* 📝 Contract Summary Details Card */}
        <div className="space-y-6">
          {currentStatus === "pending" && (
            <div className="flex gap-4 pt-2">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => setShowRejectModal(true)}
                className="flex-1 py-5 bg-white border-2 border-red-100 hover:bg-red-50 text-red-600 rounded-[1.5rem] font-black uppercase tracking-[0.15em] text-xs transition-all shadow-md active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <FiXCircle size={18} />
                Reject
              </button>
              
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => setShowAcceptModal(true)}
                className="flex-1 py-5 bg-green-600 hover:bg-green-700 text-white rounded-[1.5rem] font-black uppercase tracking-[0.15em] text-xs transition-all shadow-xl shadow-green-200 active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <FiCheckCircle size={18} />
                Accept Terms
              </button>
            </div>
          )}

          <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6">
            <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
              <FiFileText className="text-orange-600" /> Deal Terms
            </h3>

            {/* Item Title */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Item / Service Name</label>
              <div className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl py-4 px-5 font-bold text-gray-900">
                {contractData.itemTitle}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Agreement Conditions</label>
              <div className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl py-4 px-5 font-bold text-gray-900 min-h-[90px] whitespace-pre-wrap text-sm">
                {contractData.itemDescription}
              </div>
            </div>

            {/* Price */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Agreed Amount</label>
              <div className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl py-4 px-5 font-bold text-gray-900 flex items-center gap-2 text-lg text-orange-600">
                <FiDollarSign size={18} />
                {Number(contractData.price).toLocaleString()} ₦
              </div>
            </div>
          </div>

          {/* Parties Involved Card */}
          <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6">
            <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
              <FiUser className="text-orange-600" /> Parties Involved
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1">Initiator (Party A)</span>
                <p className="font-extrabold text-gray-900 text-sm">{contractData.myName}</p>
                <p className="text-xs text-gray-500 font-medium truncate mt-0.5">{contractData.myEmail}</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1">Recipient (Party B)</span>
                <p className="font-extrabold text-gray-900 text-sm">{contractData.otherPartyName}</p>
                <p className="text-xs text-gray-500 font-medium truncate mt-0.5">{contractData.otherPartyEmail}</p>
              </div>
            </div>
          </div>

          {currentStatus !== "pending" && (
            <button
              type="button"
              onClick={() => navigate("/connect")}
              className="w-full py-5 bg-gray-900 hover:bg-black text-white rounded-[1.5rem] font-black uppercase tracking-[0.2em] text-sm transition-all shadow-xl shadow-gray-300 active:scale-95 flex items-center justify-center gap-3"
            >
              Back to Dashboard
            </button>
          )}
        </div>

        <p className="text-center mt-10 text-[10px] font-black text-gray-300 uppercase tracking-widest">
          Secure Contracts • Instant Enforcement • OurASAP
        </p>
      </div>

      {/* ✅ Accept Confirmation Modal */}
      {showAcceptModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] p-8 max-w-sm w-full shadow-2xl border border-gray-100 space-y-6 animate-in fade-in zoom-in duration-200 text-center">
            <div className="w-16 h-16 bg-green-50 text-green-600 rounded-3xl mx-auto flex items-center justify-center shadow-inner">
              <FiCheckCircle size={32} />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-black text-gray-900">Accept Terms?</h3>
              <p className="text-xs font-bold text-gray-500 leading-relaxed">
                Are you sure you want to accept this agreement? This action binds the contract securely.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowAcceptModal(false)}
                className="flex-1 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-2xl font-black uppercase tracking-widest text-[10px] transition active:scale-95"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleConfirmAccept}
                className="flex-1 py-4 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] transition shadow-lg shadow-green-100 active:scale-95 disabled:opacity-50"
              >
                {isSubmitting ? "Processing..." : "Yes, Accept"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ⚠️ Reject Confirmation Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={handleConfirmReject} className="bg-white rounded-[2.5rem] p-8 max-w-sm w-full shadow-2xl border border-gray-100 space-y-6 animate-in fade-in zoom-in duration-200">
            <div className="w-16 h-16 bg-red-50 text-red-600 rounded-3xl mx-auto flex items-center justify-center shadow-inner">
              <FiAlertTriangle size={32} />
            </div>
            
            <div className="space-y-2 text-center">
              <h3 className="text-xl font-black text-gray-900">Decline Contract?</h3>
              <p className="text-xs font-bold text-gray-500 leading-relaxed">
                Please provide a brief reason for rejecting these terms.
              </p>
            </div>

            <div className="space-y-2">
              <textarea
                required
                rows="3"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason or requested changes..."
                className="w-full bg-gray-50 border-2 border-gray-100 focus:border-red-500 focus:bg-white rounded-2xl p-4 font-bold text-xs text-gray-900 transition-all outline-none resize-none"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowRejectModal(false)}
                className="flex-1 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-2xl font-black uppercase tracking-widest text-[10px] transition active:scale-95"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-4 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] transition shadow-lg shadow-red-100 active:scale-95 disabled:opacity-50"
              >
                {isSubmitting ? "Processing..." : "Confirm Reject"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ReviewContract;